import { render, html, nothing } from "lit-html";
import { stateful } from "../../../shared/components/element-builder";
import { repeat } from "lit-html/directives/repeat.js";
import { classMap } from "lit-html/directives/class-map.js";
import { translate } from "../../../shared/l10n/translate";
import { travelData } from "../../state/travelData";
import { data } from "@escapio/shared/state/payload";
import {
	isAtLeast,
	SIZE_TABLET,
	unwatch as unwatchScreenSize,
	watch as watchScreenSize,
} from "../../dom/screen";
import { visible } from "../../../shared/event/visibility-observer";
import { sendOncePerKey as sendMetricOncePerKey } from "../../tracker/Metric";
import { trackClickout, trackDirectLink } from "../../tracker/Clickout";
import { addEventTrackerData } from "../../tracker/EventQueue";
import {
	getEscapioDiscount,
	hasEscapioRate,
} from "@escapio/portal/state/escapio-deals-helpers";
import { getFormatter } from "../../../shared/l10n/currency";
import { getDiscount } from "../get-discount";
import { getOfferUrl, linkTargetForProfile } from "./get-offer-url";
import { TYPE_CITY, TYPE_COUNTRY, TYPE_REGION } from "../GeoTypeConstants";
import { getDirectLinkWithTravelData } from "../get-direct-link-with-traveldata";
import { scrollToCompletelyVisible } from "@escapio/portal/dom/smoothScroll";
import { track } from "@escapio/portal/tracker/EventTracker";
import { PAGE_TYPE_FAVORITES, pageType } from "@escapio/portal/state/pageType";
import { hasDirectLink } from "@escapio/portal/state/DirectLink";
import "./ContextCriteria";
import "./hotel-element/MobileDescription";
import "./hotel-element/MobileStage";
import "./hotel-element/DesktopStage";
import "./hotel-element/DesktopHeader";
import "../escapio-deals/DiscountInfo";

const hotelTypes = data.hotelTypes || {};

const TRACK_CATEGORY = pageType === PAGE_TYPE_FAVORITES ? "Merkliste" : "Liste";

class Hotel extends HTMLElement {
	static getInitialState() {
		return {
			hotel: null,
			hotelTypeFilter: [],
			showPictureDescription: true,
			listSorting: null,
		};
	}

	connectedCallback() {
		this.render();
		watchScreenSize(this, this.render.bind(this));
	}

	disconnectedCallback() {
		unwatchScreenSize(this);
	}

	get bestOffer() {
		return this.hotel.state.prices.hasOffers
			? this.hotel.state.prices.offers[0]
			: null;
	}

	get furtherOffers() {
		if (this.hotel.state.prices.hasOffers) {
			return this.hotel.state.prices.offers
				.slice(1) // Take out the best offer
				.slice(0, this.hasDirectLink ? 1 : 2); // Take one or two depending on EDL
		}
		return [];
	}

	get discountEscapioRate() {
		return getEscapioDiscount(this.hotel);
	}

	get discountPriceCompare() {
		return getDiscount(this.hotel.state.prices.offers);
	}

	get referenceOffer() {
		return this.discountPriceCompare !== null
			? this.hotel.state.prices.offers[1]
			: null;
	}

	get hasDirectLink() {
		return hasDirectLink(this.hotel);
	}

	get directLink() {
		return this.hasDirectLink
			? getDirectLinkWithTravelData(this.hotel.direct_link)
			: null;
	}

	get isNearby() {
		return this.hotel.distance !== -1;
	}

	render() {
		render(
			isAtLeast(SIZE_TABLET) ? this.templateDesktop : this.templateMobile,
			this,
			{ host: this },
		);
	}

	get templateMobile() {
		const isNonEscapioDealTopHotel =
			this.hotel.state.isTopHotel && !hasEscapioRate(this.hotel);

		const imageBannerText = isNonEscapioDealTopHotel
			? translate("top_hotel_our_recommendation")
			: hasEscapioRate(this.hotel)
				? this.discountEscapioRate
					? html`${translate(
								"escapio_deal_discount_text",
								this.discountEscapioRate,
							)}
							<esc-escapio-deal-discount-info></esc-escapio-deal-discount-info>`
					: translate("hotel_recommendation_escapio_offer")
				: this.discountPriceCompare
					? translate("hotel_list_discount_info", this.discountPriceCompare)
					: null;

		return html`
			<esc-hotel-list-hotel-stage-mobile
				.hotel="${this.hotel}"
				.onhold="${this.hotel.state.prices.isLoading}"
				track-category="${TRACK_CATEGORY}"
				.profileLinkHandler="${this.onStagePictureClick.bind(this)}"
			></esc-hotel-list-hotel-stage-mobile>
			${imageBannerText
				? html`<div
						class="${classMap({
							"hotel-list-item__image-banner": true,
							"hotel-list-item__image-banner--red": !isNonEscapioDealTopHotel,
							"hotel-list-item__image-banner--black": isNonEscapioDealTopHotel,
						})}"
					>
						${imageBannerText}
					</div>`
				: nothing}
			<a href=${this.hotel.path} target=${linkTargetForProfile}
				><esc-hotel-list-hotel-description-mobile
					.hotel=${this.hotel}
					@click="${() => this.trackProfileClick("Hotelname")}"
				></esc-hotel-list-hotel-description-mobile
			></a>
			${this.escapioDealTitle
				? html`<div class="hotel-list-item__escapio-deal__title">
						<strong class="hotel-list-item__escapio-deal__title-header"
							><i class="fa fa-tags"></i> ${translate(
								"escapio_deal_title",
							)}</strong
						>
						${this.escapioDealTitle}
					</div>`
				: nothing}
			${this.templateMobileCTA}
		`;
	}

	get templateMobileCTA() {
		if (this.hotel.state.prices.isLoading) {
			return html`<div class="hotel-list-item-mobile__offers">
				<div
					class="hotel-list-item-mobile__offer hotel-list-item-mobile__offer--cta"
				>
					<div class="content-placeholder"></div>
				</div>
			</div>`;
		}

		if (this.hotel.state.prices.hasOffers) {
			return this.templateMobileOffers;
		}

		if (!travelData.hasDates) {
			return html`<div class="hotel-list-item-mobile__offers">
				<a
					class="hotel-list-item-mobile__offer hotel-list-item-mobile__offer--cta action-button"
					href="#search-panel"
					@click=${this.triggerCalendar}
				>
					${translate("hotel_check_availability")}
				</a>
			</div>`;
		}

		if (this.hasDirectLink) {
			return html`<div class="hotel-list-item-mobile__offers">
				<a
					class="hotel-list-item-mobile__offer hotel-list-item-mobile__offer--cta action-button"
					href=${this.directLink}
					target="_blank"
					rel="nofollow noopener"
					@click=${this.getDirectLinkClickHandler(1, true)}
				>
					${translate("hotel_direct_link_website")}
				</a>
			</div>`;
		}

		return html`<div class="hotel-list-item-mobile__price-error">
			${translate("hotel_list_offers_error_loading_price")}
		</div>`;
	}

	get templateMobileOffers() {
		const format = rate => {
			const formatCurrency = getFormatter(travelData.currency);
			return formatCurrency(rate);
		};

		return html`<div class="hotel-list-item-mobile__offers">
			<a
				class="hotel-list-item-mobile__offer hotel-list-item-mobile__offer--cta action-button"
				href=${getOfferUrl(this.bestOffer, this.hotel.path)}
				target="_blank"
				rel="nofollow noopener"
				@click=${this.getClickoutHandler(
					this.bestOffer.provider,
					this.hotel.state.prices.providers[this.bestOffer.provider].name,
					this.bestOffer.total_rate,
					1,
				)}
			>
				<span class="hotel-list-item-mobile__offer-provider ellipsis">
					${this.hotel.state.prices.providers[this.bestOffer.provider].name}
				</span>
				<span class="hotel-list-item-mobile__offer-price">
					${this.hotel.state.prices.isMinPrice
						? html`<small>${translate("basic_from")} </small>`
						: nothing}
					${format(this.bestOffer.total_rate)}
				</span>
			</a>

			${this.furtherOffers.slice(0, 1).map(
				offer =>
					html`<a
						class="hotel-list-item-mobile__offer hotel-list-item-mobile__offer--link inverted-action-button"
						href=${getOfferUrl(offer, this.hotel.path)}
						target="_blank"
						rel="nofollow noopener"
						@click=${this.getClickoutHandler(
							offer.provider,
							this.hotel.state.prices.providers[offer.provider].name,
							offer["total_rate"],
							2,
						)}
					>
						<span class="hotel-list-item-mobile__offer-provider ellipsis">
							${this.hotel.state.prices.providers[offer.provider].name}
						</span>
						<span class="hotel-list-item-mobile__offer-price">
							${format(offer.total_rate)}
						</span>
					</a>`,
			)}
			${this.hasDirectLink
				? html`<a
						class="hotel-list-item-mobile__offer hotel-list-item-mobile__offer--direct-link ${classMap(
							{ "inverted-action-button": !this.furtherOffers.length > 0 },
						)}"
						href=${this.directLink}
						target="_blank"
						rel="nofollow noopener"
						@click=${this.getDirectLinkClickHandler(
							this.hotel.state.prices.offers.length === 1 ? 2 : 3,
						)}
					>
						${translate("hotel_direct_link_website")}
						${this.furtherOffers.length > 0
							? html`<span class="fa fa-angle-right fa--space-before"></span>`
							: nothing}
					</a>`
				: nothing}
		</div>`;
	}

	get templateDesktop() {
		return html`<div class="hotel-list-item-desktop__container">
				<esc-hotel-list-hotel-stage-desktop
					.hotel=${this.hotel}
					.type=${this.hotel.state.type}
					.isLoading=${this.hotel.state.prices.isLoading}
					.profileLinkHandler="${this.onStagePictureClick.bind(this)}"
				></esc-hotel-list-hotel-stage-desktop>

				<div class="hotel-list-item-desktop__data">
					<a
						class="hotel-list-item-desktop__header"
						href=${this.hotel.path}
						target=${linkTargetForProfile}
					>
						<esc-hotel-list-hotel-header-desktop
							.hotel=${this.hotel}
							@click="${() => this.trackProfileClick("Hotelname")}"
						></esc-hotel-list-hotel-header-desktop>
					</a>
					${this.templateDesktopOffers}
				</div>
			</div>
			<div class="hidden-mobile">${this.renderContextCriteria()}</div>`;
	}

	get templateDesktopOffers() {
		if (this.hotel.state.prices.isLoading) {
			return html`<div
				class="hotel-list-item-desktop__min-price-box content-placeholder"
			></div>`;
		}
		const text = html`${this.escapioDealTitle
			? html`<div class="hotel-list-item__escapio-deal__title">
					<strong class="hotel-list-item__escapio-deal__title-header"
						><i class="fa fa-tags"></i> ${translate(
							"escapio_deal_title",
						)}</strong
					>
					${this.escapioDealTitle}
				</div>`
			: this.hotel.excerpt
				? html`<div class="line-clamp">
						<span class="fa fa-check fa--space-after"></span>${this.hotel
							.excerpt}
					</div>`
				: nothing}`;

		if (this.hotel.state.prices.hasOffers) {
			const format = getFormatter(travelData.currency);
			const templateFurtherOffers = !hasEscapioRate(this.hotel)
				? html`<div class="hotel-list-item-desktop__extended-offers">
						${this.hasDirectLink
							? html`
									<a
										class="hotel-list-item-desktop__extended-offers__link"
										href=${this.directLink}
										target="_blank"
										rel="nofollow noopener"
										@click=${this.getDirectLinkClickHandler(2)}
									>
										${translate("hotel_direct_link_website")}<span
											class="fa fa-angle-right fa--space-before"
										></span>
									</a>
								`
							: nothing}
						${this.furtherOffers.reverse().map(
							(offer, index) => html`
								<a
									class="hotel-list-item-desktop__extended-offers__link"
									href=${getOfferUrl(offer, this.hotel.path)}
									target="_blank"
									rel="nofollow noopener"
									@click=${this.getClickoutHandler(
										offer["provider"],
										this.hotel.state.prices.providers[offer["provider"]].name,
										offer["total_rate"],
										this.hasDirectLink ? 3 : index + 2,
									)}
								>
									<strong
										class="hotel-list-item-desktop__extended-offers__price"
									>
										${format(offer["total_rate"])}
									</strong>
									${this.hotel.state.prices.providers[offer["provider"]]
										.name}<span
										class="fa fa-angle-right fa--space-before"
									></span>
								</a>
							`,
						)}
					</div>`
				: nothing;
			const discountText = this.discountEscapioRate
				? html`${translate(
							"escapio_deal_discount_text_short",
							this.discountEscapioRate,
						)}
						<esc-escapio-deal-discount-info></esc-escapio-deal-discount-info>`
				: this.discountPriceCompare
					? translate(
							"hotel_list_discount_info_short",
							this.discountPriceCompare,
						)
					: null;
			return html`${text}${templateFurtherOffers}
				<a
					class="hotel-list-item-desktop__min-price-box hotel-list-item-desktop__min-price-box--with-offer"
					href=${getOfferUrl(this.bestOffer, this.hotel.path)}
					target="_blank"
					rel="nofollow noopener"
					@click=${this.getClickoutHandler(
						this.bestOffer.provider,
						this.hotel.state.prices.providers[this.bestOffer.provider].name,
						this.bestOffer.total_rate,
						1,
					)}
				>
					<div class="provider">
						<div class="provider-name">
							${this.hotel.state.prices.providers[this.bestOffer.provider].name}
						</div>

						${discountText
							? html`<div class="discount">${discountText}</div>`
							: nothing}
					</div>
					<div class="prices">
						${this.referenceOffer
							? html`<div class="most-expensive">
									${format(this.referenceOffer.total_rate)}
								</div>`
							: nothing}
						<div class="best-price">
							${this.hotel.state.prices.isMinPrice
								? html`<small>${translate("basic_from")}</small> `
								: nothing}
							${format(this.bestOffer.total_rate)}
						</div>
						${travelData.hasDates && travelData.nights > 1
							? html`<div class="total-offer-price">
									${translate(
										"basic_price_per_night",
										format(this.bestOffer.total_rate / travelData.nights),
									)}
								</div>`
							: nothing}
					</div>
					<div class="min-price-button action-button">
						${translate("basic_to_the_offer")}
					</div>
				</a>`;
		}

		if (!travelData.hasDates) {
			return html`${text}
				<div class="hotel-list-item-desktop__min-price-box">
					<a
						class="min-price-button action-button"
						href="#search-panel"
						@click=${this.triggerCalendar}
					>
						${translate("hotel_check_availability")}
					</a>
				</div>`;
		}

		return this.hasDirectLink
			? html`${text}
					<div class="hotel-list-item-desktop__min-price-box">
						<a
							class="min-price-button action-button"
							href=${this.directLink}
							target="_blank"
							rel="nofollow noopener"
							@click=${this.getDirectLinkClickHandler(1, true)}
						>
							${translate("hotel_direct_link_website")}
						</a>
					</div>`
			: html`${text}
					<div
						class="hotel-list-item-desktop__min-price-box hotel-list-item-desktop__price-error"
					>
						${translate("hotel_list_offers_error_loading_price")}
					</div>`;
	}

	renderContextCriteria() {
		return repeat(
			this.hotelTypeFilter.filter(id =>
				this.hotel.contextCriteria.hasOwnProperty(id),
			),
			id => id,
			id => {
				const hotelType = hotelTypes.find(type => type.value == id);
				const hotelTypeName = hotelType ? hotelType.singular : "";
				const hotelTypeData = this.hotel.contextCriteria[id];
				return html`<esc-hotel-list-content-criteria
					.hotel=${this.hotel}
					.image="${hotelTypeData.image}"
					.imageAlt="${hotelTypeName} ${this.hotel.name} - ${this.hotel
						.city_name}, ${this.hotel.country_name}"
					.imageOnHold=${this.hotel.state.prices.isLoading}
					.title=${hotelTypeData.title}
					.text=${hotelTypeData.text}
					.hotelTypeName=${hotelType ? hotelType.singular : null}
				></esc-hotel-list-content-criteria>`;
			},
		);
	}

	onStagePictureClick(currentPicture) {
		this.trackProfileClick(`Bild (${currentPicture + 1})`);
	}

	trackProfileClick(context) {
		addEventTrackerData(
			TRACK_CATEGORY,
			"Klick Profil",
			`${context} - ${this.hotel.id} | ${this.hotel.name}`,
			null,
		);
	}

	getDirectLinkClickHandler(position, isDirectLinkOnly) {
		return () =>
			trackDirectLink(TRACK_CATEGORY, this.hotel, position, isDirectLinkOnly);
	}

	getClickoutHandler(...args) {
		const hotel = Object.assign({}, this.hotel);
		if (data.geo_path?.[TYPE_CITY]) {
			Object.assign(hotel, {
				city_name: data.geo_path[TYPE_CITY]?.title,
				region_name: data.geo_path[TYPE_REGION]?.title,
				country_name: data.geo_path[TYPE_COUNTRY]?.title,
			});
		}
		return trackClickout.bind(null, TRACK_CATEGORY, hotel, ...args);
	}

	async triggerCalendar(event) {
		event.preventDefault();
		const panel = document.getElementById("search-panel");
		track("Reisedaten", "Verfügbarkeit prüfen", TRACK_CATEGORY);
		await scrollToCompletelyVisible(panel);
		panel.querySelector("esc-search-panel-inputs").openCalendar();
	}

	async propertyChangedCallback(name) {
		if (name === "hotel" && !this.hotel.state.prices.isLoading) {
			await visible(this);

			const url = new URL(this.hotel.impression_url);
			const position = this.hotel.state.position || 1;
			const key = `${this.hotel.id}/${this.listSorting}`;
			sendMetricOncePerKey(key, "hotel-list-impression", position, url);
		}
	}

	get escapioDealTitle() {
		return (
			hasEscapioRate(this.hotel) &&
			(this.hotel.escapio_deal?.description || this.hotel.escapio_deal?.title)
		);
	}
}

customElements.define("esc-hotel", stateful(Hotel));
